import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { useAuth } from './useAuth';

export interface SessionAiParticipant {
  id: number;
  sessionId: string;
  personaId: string;
  personaName: string;
  personaIcon: string;
  personaCategory: string;
  addedBy: number | null;
  addedAt: string;
  isActive: boolean;
  quantity: number;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface AiParticipantTypingStatus {
  personaId: string;
  isTyping: boolean;
  action?: 'thinking' | 'coding' | 'reviewing' | 'idle';
}

// Fetch AI participants for a session
export function useSessionAiParticipants(sessionId: string) {
  const { authState } = useAuth();

  return useQuery({
    queryKey: ['session-ai-participants', sessionId],
    queryFn: async (): Promise<SessionAiParticipant[]> => {
      const res = await fetch(`/_api/sessions/ai-participants/list?sessionId=${sessionId}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 404) return []; // No participants yet
        throw new Error('Failed to fetch AI participants');
      }
      const data = await res.json();
      return data.participants || [];
    },
    enabled: !!sessionId && authState.type === 'authenticated',
    staleTime: 30000,
    retry: 1,
  });
}

// Add an AI persona as participant
export function useAddAiParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, personaId }: { sessionId: string; personaId: string }) => {
      const res = await fetch(`/_api/sessions/ai-participants/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId, personaId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to add AI participant');
      }
      return res.json();
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: ['session-ai-participants', sessionId] });
    },
  });
}

// Remove an AI persona from participants
export function useRemoveAiParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      participantId,
    }: {
      sessionId: string;
      participantId: string;
    }) => {
      const res = await fetch(`/_api/sessions/ai-participants/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId, participantId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to remove AI participant');
      }
      return res.json();
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: ['session-ai-participants', sessionId] });
    },
  });
}

// Activate an AI participant to start working autonomously
export function useActivateAiParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, personaId }: { sessionId: string; personaId: string }) => {
      const res = await fetch(`/_api/sessions/ai-participants/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId, personaId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to activate AI participant');
      }
      return res.json();
    },
    onSuccess: (_, { sessionId }) => {
      // Refresh AI chat messages to show the AI's introduction
      // AI chat history uses queryKey ['ai', 'history', sessionId]
      queryClient.invalidateQueries({ queryKey: ['ai', 'history', sessionId] });
      queryClient.invalidateQueries({ queryKey: ['session-ai-participants', sessionId] });
    },
  });
}
